import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  getSkillReferenceTableRows,
  skillRangeHpsLabel,
  SWISS_PADEL_LEVEL_OPTIONS,
  PLAYTOMIC_BAND_OPTIONS,
} from '../../constants/playerSignup';

const BRAND = { blue: '#043663', gray: '#E5E7EB' };

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 1rem;
  @media (min-width: 640px) {
    align-items: center;
  }
`;

const Panel = styled.div`
  width: 100%;
  max-width: min(720px, 100vw - 2rem);
  max-height: min(90vh, 720px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border: 1px solid ${BRAND.gray};
`;

const Head = styled.div`
  padding: 1.25rem 1.25rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const Title = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
`;

const Intro = styled.p`
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.5;
  margin: 0;
`;

const TableWrap = styled.div`
  overflow: auto;
  padding: 0.75rem 1rem 1rem;
  flex: 1;
  min-height: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.65rem 0.5rem;
  border-bottom: 2px solid #e5e7eb;
  color: ${BRAND.blue};
  font-weight: 700;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  vertical-align: bottom;
`;

const Td = styled.td`
  padding: 0.65rem 0.5rem;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  line-height: 1.45;
  vertical-align: top;
`;

const Foot = styled.div`
  padding: 0.75rem 1rem 1.25rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const CloseBtn = styled.button`
  width: 100%;
  padding: 0.65rem 1rem;
  background: ${BRAND.blue};
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #032a4a;
  }
`;

/**
 * Full-screen-style modal: Helvetic Padel Series skill level ↔ Playtomic ↔ Swiss Tennis Padel.
 */
export default function SkillReferenceModal({ open, onClose }) {
  const { t } = useTranslation();
  if (!open) return null;

  const rows = getSkillReferenceTableRows();

  return (
    <Overlay
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <Panel
        role="dialog"
        aria-modal="true"
        aria-labelledby="skill-ref-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <Head>
          <Title id="skill-ref-modal-title">{t('playerRegister.skillMappingModalTitle')}</Title>
          <Intro>{t('playerRegister.skillMappingModalIntro')}</Intro>
        </Head>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>{t('playerRegister.skillMappingColHps')}</Th>
                <Th>{t('playerRegister.skillMappingColPlaytomic')}</Th>
                <Th>{t('playerRegister.skillMappingColSwiss')}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const pt = row.playtomicId
                  ? PLAYTOMIC_BAND_OPTIONS.find((o) => o.id === row.playtomicId)
                  : null;
                const swissOpts = SWISS_PADEL_LEVEL_OPTIONS.filter((s) => row.swissLevels.includes(s.level));
                return (
                  <tr key={row.skillRange}>
                    <Td>
                      <strong>{skillRangeHpsLabel(row.skillRange)}</strong>
                    </Td>
                    <Td>{pt ? t(pt.labelKey) : '—'}</Td>
                    <Td>
                      {swissOpts.length
                        ? swissOpts.map((s) => t(s.labelKey)).join(' · ')
                        : '—'}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
        <Foot>
          <CloseBtn type="button" onClick={onClose}>
            {t('playerRegister.skillMappingModalClose')}
          </CloseBtn>
        </Foot>
      </Panel>
    </Overlay>
  );
}
